import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FolderOpen, Clock, AlertTriangle, CheckCircle2, ListTodo, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { NewPraticaDialog } from "@/components/dashboard/NewPraticaDialog";
import { PraticaCard } from "@/components/dashboard/PraticaCard";
import { RichiesteCall } from "@/components/RichiesteCall";
import { RichiestePartner, FornitoriBox } from "@/components/PartnerTeam";
import { OnboardingBanner } from "@/components/OnboardingBanner";
import { MiniMappaBandi } from "@/components/MiniMappaBandi";
import { KanbanPratiche } from "@/components/dashboard/KanbanPratiche";
import { AttivitaFeed } from "@/components/AttivitaFeed";
import { FascicoloGara } from "@/components/FascicoloGara";
import { TourPrimoAccesso } from "@/components/TourPrimoAccesso";
import { MioLavoro } from "@/components/dashboard/MioLavoro";
import { ClassificaEsg } from "@/components/dashboard/ClassificaEsg";
import { AnomalieRendicontazione } from "@/components/dashboard/AnomalieRendicontazione";
import { LayoutGrid, Columns3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATS = [
  { key: "pratiche_attive", label: "Pratiche attive", icon: FolderOpen, cls: "text-[#003087] bg-blue-50" },
  { key: "in_scadenza_30gg", label: "In scadenza (30 gg)", icon: Clock, cls: "text-amber-700 bg-amber-50" },
  { key: "documenti_mancanti", label: "Documenti obbligatori mancanti", icon: AlertTriangle, cls: "text-red-700 bg-red-50" },
  { key: "miei_task_aperti", label: "Task aperti di mia competenza", icon: ListTodo, cls: "text-purple-700 bg-purple-50" },
  { key: "inviate", label: "Domande inviate", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50" },
];

export default function Dashboard() {
  const [pratiche, setPratiche] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [tour, setTour] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(false);
  const [vista, setVista] = useState(() => localStorage.getItem("pratiche_vista") || "griglia");
  const [sp] = useSearchParams();
  const [ordine, setOrdine] = useState(() => sp.get("ordine") || localStorage.getItem("pratiche_ordine") || "scadenza");
  useEffect(() => { if (sp.get("ordine")) setOrdine(sp.get("ordine")); }, [sp]);
  const [fasi, setFasi] = useState([]);
  useEffect(() => { localStorage.setItem("pratiche_vista", vista); localStorage.setItem("pratiche_ordine", ordine); }, [vista, ordine]);
  useEffect(() => { api.get("/bando/riferimenti").then((r) => setFasi(r.data.fasi || [])); }, []);
  const ordinate = [...pratiche].sort((a, b) => ordine === "scadenza" ? (a.scadenza_bando || "9").localeCompare(b.scadenza_bando || "9") : ordine === "prontezza" ? (b.progress?.percentuale || 0) - (a.progress?.percentuale || 0) : ordine === "aggiornamento" ? (b.updated_at || "").localeCompare(a.updated_at || "") : (a.titolo || "").localeCompare(b.titolo || ""));
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = async (onlyMine) => {
    const [p, s] = await Promise.all([api.get("/pratiche", { params: onlyMine ? { mine: true } : {} }), api.get("/dashboard/stats")]);
    setPratiche(p.data); setStats(s.data); setLoading(false);
  };
  useEffect(() => { load(mine); }, [mine]);

  return (
    <AppShell title="Pratiche di gara" subtitle={`Benvenuto, ${user?.name || ""}`}
      actions={<div className="flex gap-2"><Button variant="outline" onClick={() => setTour(true)} className="rounded-md" data-testid="tour-apri"><Compass className="h-4 w-4 mr-2" /> Tour</Button><Button onClick={() => setOpen(true)} className="bg-[#003087] hover:bg-[#0070BA] rounded-md shadow-sm" data-testid="new-pratica-button"><Plus className="h-4 w-4 mr-2" /> Nuova pratica</Button></div>}>
      <OnboardingBanner />
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="dashboard-stats">
        {STATS.map(({ key, label, icon: Icon, cls }, i) => (
          <div key={key} className="bg-white border border-slate-200 rounded-xl p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-${key}`}>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${cls}`}><Icon size={18} strokeWidth={1.5} /></div>
            <div className="mt-4 font-heading text-3xl font-semibold text-slate-900">{stats ? stats[key] : "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{label}</div>
          </div>
        ))}
      </section>
      <MioLavoro />
      <TourPrimoAccesso forza={tour} onClose={() => setTour(false)} />
      <ClassificaEsg />

      <section className="mt-8" data-testid="dashboard-mappa">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Calendario bandi</p>
        <MiniMappaBandi />
      </section>

      <AnomalieRendicontazione />

      <section className="mt-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Fascicoli</p>
            <h2 className="font-heading text-2xl font-medium text-slate-900">Tutte le pratiche</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-medium" data-testid="pratiche-filter">
              <button onClick={() => setMine(false)} className={`px-3 py-1.5 rounded-md transition-colors ${!mine ? "bg-[#003087] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="filter-all-button">Tutte</button>
              <button onClick={() => setMine(true)} className={`px-3 py-1.5 rounded-md transition-colors ${mine ? "bg-[#003087] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="filter-mine-button">Le mie pratiche</button>
            </div>
            <select value={ordine} onChange={(e) => setOrdine(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid="pratiche-ordine"><option value="scadenza">Per scadenza</option><option value="prontezza">Per prontezza</option><option value="aggiornamento">Ultime aggiornate</option><option value="titolo">Per titolo</option></select>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="pratiche-vista"><button onClick={() => setVista("griglia")} className={`p-1.5 rounded-md ${vista === "griglia" ? "bg-[#003087] text-white" : "text-slate-500"}`} title="Griglia" data-testid="vista-griglia"><LayoutGrid size={14} /></button><button onClick={() => setVista("kanban")} className={`p-1.5 rounded-md ${vista === "kanban" ? "bg-[#003087] text-white" : "text-slate-500"}`} title="Kanban per fase" data-testid="vista-kanban"><Columns3 size={14} /></button></div>
            <span className="text-sm text-slate-500" data-testid="pratiche-count">{pratiche.length} pratiche</span>
          </div>
        </div>
        {loading ? <p className="text-slate-500 text-sm">Caricamento…</p> : pratiche.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center" data-testid="pratiche-empty">
            <div className="mx-auto h-14 w-14 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400"><FolderOpen strokeWidth={1.5} /></div>
            <h3 className="font-heading text-lg font-medium text-slate-900 mt-5">{mine ? "Nessuna pratica assegnata a te" : "Nessuna pratica"}</h3>
            <p className="text-sm text-slate-500 mt-1">{mine ? "Chiedi al project manager di aggiungerti al team di una pratica." : "Crea la prima pratica per avviare il flusso di lavoro del team."}</p>
            <Button onClick={() => setOpen(true)} className="mt-6 bg-[#003087] hover:bg-[#0070BA]" data-testid="empty-new-pratica-button"><Plus className="h-4 w-4 mr-2" /> Nuova pratica</Button>
          </div>
        ) : vista === "kanban" ? <KanbanPratiche pratiche={ordinate} fasi={fasi} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="pratiche-list">
            {ordinate.map((p, i) => <PraticaCard key={p.id} pratica={p} index={i} onClick={() => navigate(`/pratiche/${p.id}`)} />)}
          </div>
        )}
      </section>

      <section className="mt-10">
        <AttivitaFeed limit={12} />
      </section>

      <section className="mt-10">
        <FascicoloGara />
      </section>

      {["admin", "project_manager", "commerciale"].includes(user?.role) && <RichiesteCall onPraticaCreata={() => load(mine)} />}
      {["admin", "project_manager", "commerciale"].includes(user?.role) && <RichiestePartner />}
      {["admin", "project_manager", "commerciale"].includes(user?.role) && <FornitoriBox canEdit />}

      <NewPraticaDialog open={open} onOpenChange={setOpen} onCreated={(p) => { setOpen(false); navigate(`/pratiche/${p.id}`); }} />
    </AppShell>
  );
}
